import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import HealingIcon from '@mui/icons-material/Healing';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import SpaIcon from '@mui/icons-material/Spa';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import FaceIcon from '@mui/icons-material/Face';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import glowBooster from '../../assets/hydraglow-large.png';
import energyBoost from '../../assets/ingredient-energt.jpg';
import proAge from '../../assets/proage-ing.png';
import hyaluMinerals from '../../assets/hyaly-ingredient.jpeg';

const products = [
    {
        id: 1,
        title: 'GLOW BOOSTER',
        subtitle: 'HYDRA GLOW',
        description: 'Soothing and moisturizing elixir with 5% Snail Slime Extract, Polyglutamic Acid, and Beta Glucan.',
        image: glowBooster,
        activeIngredients: [
            '5% Snail Slime Extract',
            'Polyglutamic Acid',
            'Beta Glucan',
            'Madecasoside'
        ],
        benefits: [
            { title: 'Healing and repair', description: 'Supports healing and repairs damaged skin cells, stimulates collagen synthesis', icon: HealingIcon },
            { title: 'Moisturizing', description: 'Deep and long lasting moisture, reduces fine lines, boosts skincare effectiveness', icon: WaterDropIcon },
            { title: 'Soothing', description: 'Calms sensitive skin, ideal for redness, rosacea or acne, boosts skin immunity', icon: SpaIcon },
            { title: 'Brightening', description: 'Helps fade dark spots, hyperpigmentation and acne scars, reduces oxidative damage', icon: WbSunnyIcon }
        ]
    },
    {
        id: 2,
        title: 'ENERGY BOOST',
        subtitle: 'BRIGHTENING SERUM',
        description: '4% Brightening serum with ultra-stable Vitamin C for radiant, even-toned skin.',
        image: energyBoost,
        activeIngredients: [
            '4% Ultra-Stable Vitamin C',
            'Ferulic Acid'
        ],
        benefits: [
            { title: 'Brightening', description: 'Reduces hyperpigmentation and dark spots, evens skin tone', icon: WbSunnyIcon },
            { title: 'Collagen Boost', description: 'Stimulates collagen production for firmer, tighter skin', icon: TrendingUpIcon },
            { title: 'Antioxidant Protection', description: 'Neutralizes free radicals, prevents oxidative damage, increases stability and efficacy', icon: ShieldIcon }
        ]
    },
    {
        id: 3,
        title: 'PRO AGE EXPERT',
        subtitle: 'ANTI-WRINKLE SERUM',
        description: 'Exclusive anti-wrinkle serum with Peptide Complex for youthful, firm skin.',
        image: proAge,
        activeIngredients: [
            'Nutripeptides®',
            'Argireline',
            'Son®-Coll',
            'Trehalose',
            'Hyaluronic Acid',
            'Hydrolysed Glycosamine Glycocans'
        ],
        benefits: [
            { title: 'Anti-Aging', description: 'Reduces wrinkles and fine lines, prevents new wrinkles, smoothens expression lines', icon: FaceIcon },
            { title: 'Firming', description: 'Improves skin elasticity, boosts collagen, enhances skin barrier', icon: TrendingUpIcon },
            { title: 'Hydration & Repair', description: 'Deep hydration, boosts skin regeneration, soothes irritated skin, promotes healing', icon: RefreshIcon }
        ]
    },
    {
        id: 4,
        title: 'HYALU MINERALS',
        subtitle: 'HYDRO-SERUM',
        description: 'Smoothing hydro-serum with 2% Hyaluronic Acid and 1.5% Twainmoist for intensive hydration.',
        image: hyaluMinerals,
        activeIngredients: [
            '2% Hyaluronic Acid (HMW)',
            '1.5% Twainmoist®',
            'Trehalose',
            'Sodium Lactate',
            'Propanediol',
            'Fermented Essence'
        ],
        benefits: [
            { title: 'Deep Hydration', description: 'Multi-layer hydration for surface, 2nd layer and deepest layer, retains moisture', icon: WaterDropIcon },
            { title: 'Plumping & Smoothing', description: 'Keeps skin plump, reduces sagging, refines texture, prevents dryness', icon: AutoAwesomeIcon },
            { title: 'Repair & Renewal', description: 'Stimulates collagen and elasticity, enhances absorption, strengthens skin barrier', icon: HealingIcon }
        ]
    }
];

export const Page6 = () => {
    const getBackgroundColor = (productId: number) => {
        switch (productId) {
            case 1: // HYDRA GLOW - Light Blue
                return 'rgba(230, 245, 255, 1)';
            case 2: // ENERGY BOOST - Orange/Yellow
                return 'rgba(255, 245, 230, 1)';
            case 3: // PRO AGE EXPERT - Purple
                return 'rgba(245, 240, 255, 1)';
            case 4: // HYALU MINERALS - Teal/Green
                return 'rgba(230, 250, 245, 1)';
            default:
                return '#FFFFFF';
        }
    };

    return (
        <Container
            maxWidth={false}
            sx={{
                paddingTop: '50px',
                paddingBottom: '50px',
                backgroundColor: '#FFFFFF',
                borderRadius: '20px',
            }}
        >
            {products.map((product, index) => {
                const isReversed = index % 2 === 1; // Alternate: odd indices are reversed
                const backgroundColor = getBackgroundColor(product.id);

                return (
                    <Box
                        key={product.id}
                        sx={{
                            marginBottom: index < products.length - 1 ? '80px' : '0',
                            padding: {
                                xs: '20px',
                                md: '40px',
                            },
                            borderRadius: '20px',
                            backgroundColor: backgroundColor,
                            transition: 'background-color 0.5s ease',
                        }}
                    >
                        <Grid
                            container
                            spacing={2}
                            alignItems="stretch"
                            sx={{
                                marginBottom: '40px',
                                flexDirection: { xs: 'column', md: isReversed ? 'row-reverse' : 'row' }
                            }}
                        >
                            {/* Content Column */}
                            <Grid
                                item xs={12}
                                md={6}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center'
                                }}
                            >
                                <Box
                                    sx={{
                                        padding: {
                                            xs: '10px',
                                            md: '30px',
                                        },
                                        borderRadius: '36px',
                                        backgroundColor: '#fff',
                                        border: {
                                            xs: 'none',
                                            md: '1px solid rgba(221, 221, 221, 1)',
                                        },
                                        height: '100%',
                                    }}
                                >
                                    <Typography
                                        variant='h2'
                                        sx={{
                                            fontFamily: 'ClaireMurphy',
                                            fontSize: {
                                                xs: '36px',
                                                md: '48px',
                                            },
                                            fontWeight: 400,
                                            lineHeight: {
                                                xs: '42px',
                                                md: '56px',
                                            },
                                            textAlign: 'left',
                                            color: 'rgba(61, 61, 61, 1)',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        {product.title}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: 'ClaireMurphy',
                                            fontSize: {
                                                xs: '24px',
                                                md: '32px',
                                            },
                                            fontWeight: 400,
                                            textAlign: 'left',
                                            color: 'rgba(0, 48, 135, 1)',
                                            marginBottom: '20px',
                                        }}
                                    >
                                        {product.subtitle}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand',
                                            fontSize: '16px',
                                            fontWeight: 400,
                                            lineHeight: '24px',
                                            textAlign: 'left',
                                            color: 'rgba(136, 136, 136, 1)',
                                            marginBottom: '30px',
                                        }}
                                    >
                                        {product.description}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontFamily: 'Quicksand',
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            lineHeight: '24px',
                                            textAlign: 'left',
                                            color: 'rgba(61, 61, 61, 1)',
                                            marginBottom: '10px',
                                        }}
                                    >
                                        ACTIVE INGREDIENTS:
                                    </Typography>

                                    <Typography
                                        component="ul"
                                        sx={{
                                            fontFamily: 'Quicksand',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            lineHeight: '22px',
                                            textAlign: 'left',
                                            color: 'rgba(61, 61, 61, 1)',
                                            paddingLeft: '20px',
                                            margin: 0,
                                        }}
                                    >
                                        {product.activeIngredients.map((ingredient, idx) => (
                                            <li key={idx}>{ingredient}</li>
                                        ))}
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* Image Column */}
                            <Grid
                                item xs={12}
                                md={6}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start'
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: '300px',
                                        marginBottom: '20px',
                                        borderRadius: '20px',
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={product.image}
                                        alt={product.title}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '20px',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Box>
                                <Grid container spacing={1}>
                                    {product.benefits.map((benefit, fIdx) => {
                                        const IconComponent = benefit.icon;
                                        return (
                                            <Grid item xs={12} md={4} key={fIdx}>
                                                <Box
                                                    sx={{
                                                        padding: '15px',
                                                        borderRadius: '20px',
                                                        backgroundColor: '#fff',
                                                        border: '1px solid rgba(221, 221, 221, 1)',
                                                        textAlign: 'left',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'flex-start',
                                                        justifyContent: 'space-between',
                                                        height: '100%',
                                                    }}
                                                >
                                                    <IconComponent
                                                        sx={{
                                                            fontSize: '40px',
                                                            color: 'rgba(0, 48, 135, 1)',
                                                            marginBottom: '10px'
                                                        }}
                                                    />
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: 'Quicksand',
                                                                fontSize: '18px',
                                                                fontWeight: 600,
                                                                lineHeight: '26px',
                                                                color: 'rgba(61, 61, 61, 1)',
                                                                marginBottom: '10px',
                                                            }}
                                                        >
                                                            {benefit.title}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: 'Quicksand',
                                                                fontSize: '12px',
                                                                fontWeight: 400,
                                                                lineHeight: '20px',
                                                                color: 'rgba(136, 136, 136, 1)',
                                                            }}
                                                        >
                                                            {benefit.description}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                );
            })}
        </Container>
    );
};
